import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiExternalLink, FiFile } = FiIcons;

const LessonContent = ({ lesson }) => {
  const renderTextContent = (content) => {
    if (!content) return null;

    // Simple markdown-like rendering
    const lines = content.split('\n');
    const elements = [];
    
    lines.forEach((line, index) => {
      line = line.trim();
      
      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={index} className="text-3xl font-bold text-gray-900 mb-4">
            {line.substring(2)}
          </h1>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={index} className="text-2xl font-bold text-gray-900 mb-3 mt-6">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={index} className="text-xl font-bold text-gray-900 mb-2 mt-4">
            {line.substring(4)}
          </h3>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Handle lists (simplified)
        elements.push(
          <li key={index} className="text-gray-700 mb-1">
            {line.substring(2)}
          </li>
        );
      } else if (line.includes('[') && line.includes('](')) {
        // Handle links
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = line.split(linkRegex);
        const linkElements = [];
        
        for (let i = 0; i < parts.length; i += 3) {
          if (parts[i]) {
            linkElements.push(parts[i]);
          }
          if (parts[i + 1] && parts[i + 2]) {
            linkElements.push(
              <a
                key={i}
                href={parts[i + 2]}
                target="_blank"
                rel="noopener noreferrer"
                className="text-nutrition-600 hover:text-nutrition-700 underline"
              >
                {parts[i + 1]}
              </a>
            );
          }
        }
        
        elements.push(
          <p key={index} className="text-gray-700 mb-3 leading-relaxed">
            {linkElements}
          </p>
        );
      } else if (line.length > 0) {
        elements.push(
          <p key={index} className="text-gray-700 mb-3 leading-relaxed">
            {line}
          </p>
        );
      }
    });
    
    return elements;
  };

  const downloadResource = async (resource) => {
    try {
      // In a real implementation, you'd get a signed URL from Supabase Storage
      // For now, we'll just open the URL
      window.open(resource.file_url, '_blank');
    } catch (error) {
      console.error('Error downloading resource:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* Text Content */}
      {lesson.text_content && (
        <div className="prose prose-lg max-w-none">
          <div className="text-content">
            {renderTextContent(lesson.text_content)}
          </div>
        </div>
      )}

      {/* Resources */}
      {lesson.resources && lesson.resources.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiFile} className="w-5 h-5 mr-2" />
            Lesson Resources
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {lesson.resources.map((resource, index) => (
              <motion.button
                key={index}
                onClick={() => downloadResource(resource)}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-nutrition-300 hover:shadow-sm transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiDownload} className="w-5 h-5 text-nutrition-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{resource.label}</p>
                    {resource.description && (
                      <p className="text-sm text-gray-500">{resource.description}</p>
                    )}
                  </div>
                </div>
                <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-gray-400" />
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!lesson.text_content && (!lesson.resources || lesson.resources.length === 0) && (
        <div className="text-center py-12">
          <SafeIcon icon={FiFile} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No content available</h3>
          <p className="text-gray-500">
            This lesson doesn't have any written content or resources yet.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default LessonContent;